import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ICONS } from "../../icons";
import { getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead } from "../../services/api";
import { useNotificationsWS } from "../../hooks/useNotificationsWS";
import { useTranslation } from "../../context/I18nContext";

// POLL_MS is the fallback path — see useNotificationsWS's doc comment for
// why the bell doesn't rely on the WebSocket push alone.
const POLL_MS = 45000;

// timeAgo takes the "header" namespace's own t() as a parameter (it's a
// plain module-level function, not a component, so it has no hook access
// of its own) — each "{n}<unit> ago" key is a template a translator can
// reorder for their language, substituted here with a plain string
// .replace() since the result is rendered as plain text, not JSX needing
// per-part styling (contrast with common.pagination_summary, which uses
// the fuller renderTemplate/JSX helper because it highlights the numbers).
function timeAgo(iso, t) {
  if (!iso) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return t("notifications_time_just_now", "just now");
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t("notifications_time_minutes_ago", "{n}m ago").replace("{n}", diffMin);
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t("notifications_time_hours_ago", "{n}h ago").replace("{n}", diffHr);
  const diffDay = Math.floor(diffHr / 24);
  return t("notifications_time_days_ago", "{n}d ago").replace("{n}", diffDay);
}

// NotificationBell replaces the header's old static bell placeholder (a
// hardcoded "3" badge that never reflected anything real) with the actual
// system notifications feed — currently just low-stock alerts, see
// services.CheckLowStock on the backend and models.Notification's doc
// comment for why this is a single global feed rather than per-user.
export default function NotificationBell() {
  const { t } = useTranslation("header");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const containerRef = useRef(null);

  const refreshCount = useCallback(() => {
    getUnreadNotificationCount(token)
      .then((res) => setUnreadCount(res?.count || 0))
      .catch(() => {});
  }, [token]);

  const refreshList = useCallback(() => {
    getNotifications(token)
      .then((res) => setNotifications(res || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    refreshCount();
    refreshList();
    const interval = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(interval);
  }, [refreshCount, refreshList]);

  // Live push: a freshly created notification lands here almost instantly
  // when the WebSocket is available — see useNotificationsWS. Prepending
  // client-side means the very first sale that crosses a reorder point
  // shows up in the bell without waiting for the next poll tick.
  useNotificationsWS((notification) => {
    setNotifications((prev) => [notification, ...prev].slice(0, 50));
    setUnreadCount((prev) => prev + 1);
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      if (!prev) refreshList();
      return !prev;
    });
  };

  const handleItemClick = (n) => {
    if (!n.is_read) {
      markNotificationRead(token, n.id).catch(() => {});
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    if (n.product_id) {
      setOpen(false);
      navigate(`/edit-product/${n.product_id}`);
    }
  };

  const handleMarkAllRead = () => {
    markAllNotificationsRead(token).catch(() => {});
    setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnreadCount(0);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button className="notif-btn" onClick={toggleOpen}>
        <ICONS.Bell className="w-[22px] h-[22px]" />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        /* end-0 بدل right-0 عشان القائمة المنسدلة تفتح من الجهة الصح
           (تتقلب تلقائيًا مع اتجاه اللغة) */
        <div className="absolute end-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl border border-gray-100 shadow-xl z-[10000] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <span className="font-black text-sm text-gray-800">{t("notifications_title", "Notifications")}</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-[11px] font-bold text-[var(--primary-color)] hover:underline">
                {t("notifications_mark_all_read", "Mark all read")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 && (
              <p className="text-center text-gray-400 text-xs italic py-8">{t("notifications_empty", "No notifications yet.")}</p>
            )}
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleItemClick(n)}
                className={`w-full text-start px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors ${
                  n.is_read ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-gray-800">{n.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-gray-300 mt-1">{timeAgo(n.created_at, t)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
